// Aceite em lote na aba Aguardando (pedido Lokos/Laryssa, tkt SabeZap 60283, 14/10/2026).
//
// A atendente marca vários tickets e aceita todos de uma vez: vão para a caixa
// dela (Atendendo) e ela chama um por um. Regras, iguais ao aceite individual:
//   - ticket COM setor mantém o setor dele;
//   - ticket SEM setor exige escolher um setor — UMA escolha vale para todos os
//     sem setor do lote (mesma lista do modal individual: setores do usuário);
//   - com a opção "openTickets" ativa, contato que já tem atendimento aberto é
//     pulado (o modal individual bloqueia do mesmo jeito) e entra no resumo.
// Não troca de aba de propósito: a pessoa segue no Aguardando para pegar mais,
// e a chave de troca de aba varia entre instâncias (sabezap:/pressticket:).
// Componente autocontido: para levar a outra instância basta ele + o trecho
// marcado "Aceite em lote" no TicketsList e no TicketListItem.
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	MenuItem,
	Select,
	Typography
} from "@mui/material";
import LibraryAddCheck from "@mui/icons-material/LibraryAddCheck";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const semSetor = ticket => !ticket.queueId && !ticket.queue;

const BulkAcceptTickets = ({ selectMode, onSelectModeChange, selectedTickets, onClear }) => {
	const { t } = useTranslation();
	const { user } = useContext(AuthContext);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [queueId, setQueueId] = useState("");
	const [running, setRunning] = useState(false);
	const [openTicketsSetting, setOpenTicketsSetting] = useState(null);

	useEffect(() => {
		if (!dialogOpen) return;
		api.get("/settings")
			.then(({ data }) => setOpenTicketsSetting(data.find(s => s.key === "openTickets")?.value || null))
			.catch(() => setOpenTicketsSetting(null));
	}, [dialogOpen]);

	const qtd = selectedTickets.length;
	const qtdSemSetor = selectedTickets.filter(semSetor).length;
	const userQueues = user?.queues || [];
	const precisaSetor = qtdSemSetor > 0 && userQueues.length > 0;

	const fechar = () => {
		setDialogOpen(false);
		setQueueId("");
	};

	const cancelarSelecao = () => {
		onClear();
		onSelectModeChange(false);
	};

	const aceitar = async () => {
		setRunning(true);
		let ok = 0;
		const pulados = [];
		const falhas = [];

		// Um por vez: o backend dispara socket/mensagens a cada aceite e o
		// lote é pequeno (a pessoa escolhe na mão).
		for (const ticket of selectedTickets) {
			const nome = ticket.contact?.name || `#${ticket.id}`;
			if (semSetor(ticket) && !queueId) {
				pulados.push(`${nome} (${t("ticketsList.bulkAccept.noQueue", "sem setor")})`);
				continue;
			}
			try {
				if (openTicketsSetting !== "disabled" && ticket.contact?.id) {
					const { data } = await api.get(`/tickets/contact/${ticket.contact.id}/open`);
					if (data?.hasOpenTicket && data.ticket?.id !== ticket.id) {
						const quem = data.ticket?.user?.name || "?";
						pulados.push(`${nome} (${t("ticketsList.bulkAccept.alreadyOpen", "já em atendimento com")} ${quem})`);
						continue;
					}
				}
				const body = { status: "open", userId: user?.id };
				if (semSetor(ticket)) body.queueId = queueId;
				await api.put(`/tickets/${ticket.id}`, body);
				ok++;
			} catch (err) {
				falhas.push(nome);
			}
		}

		setRunning(false);
		fechar();
		onClear();
		onSelectModeChange(false);

		if (ok > 0) {
			toast.success(t("ticketsList.bulkAccept.success", "{{count}} ticket(s) aceito(s) — estão na aba Atendendo.", { count: ok }));
		}
		if (pulados.length > 0) {
			toast.warning(`${t("ticketsList.bulkAccept.skipped", "Não aceitos")}: ${pulados.join("; ")}`, { autoClose: 10000 });
		}
		if (falhas.length > 0) {
			toast.error(`${t("ticketsList.bulkAccept.failed", "Erro ao aceitar")}: ${falhas.join(", ")}`, { autoClose: 10000 });
		}
	};

	if (!selectMode) {
		return (
			<Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, py: 0.5 }}>
				<Button size="small" startIcon={<LibraryAddCheck />} onClick={() => onSelectModeChange(true)}>
					{t("ticketsList.bulkAccept.selectMany", "Selecionar vários")}
				</Button>
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, bgcolor: "action.hover" }}>
				<Typography variant="body2" sx={{ flex: 1 }}>
					{t("ticketsList.bulkAccept.selected", "{{count}} selecionado(s)", { count: qtd })}
				</Typography>
				<Button size="small" onClick={cancelarSelecao}>
					{t("ticketsList.buttons.cancel")}
				</Button>
				<Button size="small" variant="contained" disabled={qtd === 0} onClick={() => setDialogOpen(true)}>
					{t("ticketsList.bulkAccept.accept", "Aceitar")}
				</Button>
			</Box>

			<Dialog open={dialogOpen} onClose={running ? undefined : fechar} maxWidth="xs" fullWidth>
				<DialogTitle>
					{t("ticketsList.bulkAccept.title", "Aceitar {{count}} ticket(s)", { count: qtd })}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						{t("ticketsList.bulkAccept.explain", "Eles vão para a sua caixa (aba Atendendo).")}
					</Typography>
					{qtdSemSetor > 0 && userQueues.length === 0 && (
						<Alert severity="warning">
							{t("ticketsList.bulkAccept.noUserQueues", "{{count}} ticket(s) sem setor e você não tem setor vinculado: eles serão ignorados.", { count: qtdSemSetor })}
						</Alert>
					)}
					{precisaSetor && (
						<FormControl fullWidth>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
								{t("ticketsList.bulkAccept.queueFor", "{{count}} ticket(s) sem setor — o setor escolhido vale para todos eles:", { count: qtdSemSetor })}
							</Typography>
							<Select value={queueId} onChange={e => setQueueId(e.target.value)} displayEmpty size="small">
								<MenuItem value="">
									<em>{t("ticketsList.acceptModal.selectQueue")}</em>
								</MenuItem>
								{userQueues.map(q => (
									<MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={fechar} disabled={running}>
						{t("ticketsList.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={aceitar}
						disabled={running || (precisaSetor && !queueId)}
						sx={{ minWidth: 110 }}
					>
						{running ? <CircularProgress size={20} color="inherit" /> : t("ticketsList.bulkAccept.accept", "Aceitar")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

BulkAcceptTickets.propTypes = {
	selectMode: PropTypes.bool.isRequired,
	onSelectModeChange: PropTypes.func.isRequired,
	selectedTickets: PropTypes.array.isRequired,
	onClear: PropTypes.func.isRequired,
};

export default BulkAcceptTickets;
